import ticketsApi from 'api/requests/tickets';
import Text from 'components/_UI/Text';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { FullyCentered } from 'styled/alignment/Center';
import { Column } from 'styled/alignment/Column';
import colors from 'theme/colors';
import ProductSelect from './ProductSelect';
import Loader from 'components/_UI/Loader';
import breakpoints from 'theme/breakpoints';
import { useEvent } from 'context/events/Event';
import { SaleTypes } from 'modules/_common/SaleTypes';
import Paginate from 'components/_UI/Paginate';
import { ArrowLeft, ArrowRight } from 'lucide-react';

export default function ProductsToEvent({
    edit,
    availableProducts,
    productsPagination,
    isDraft,
    onProductPaginate = () => false,
    onSuccess = () => false,
    goBackFormStep = () => false,
}) {
    const [loading, setLoading] = useState(false);
    const [selectedProductIds, setSelectedProductIds] = useState([]);
    const [products, setProducts] = useState([]);
    const allProductsRef = useRef(new Map());

    const { event } = useEvent();

    useEffect(() => {
        getProducts();
    }, [event]);

    const getProducts = () => {
        setProducts(availableProducts);

        if (edit && event?.products?.length) {
            const preSelectedProductIds = event.products.map(product => product.name);
            setSelectedProductIds(preSelectedProductIds);
        }
    };

    // Accumulate products from all pages into the ref
    useEffect(() => {
        if (availableProducts?.length) {
            availableProducts.forEach(product => {
                // Only add if not already tracked (preserve user edits)
                if (!allProductsRef.current.has(product.name)) {
                    allProductsRef.current.set(product.name, product);
                }
            });
        }
    }, [availableProducts]);

    const handleSelect = (id) => {
        setSelectedProductIds((prevSelected) =>
            prevSelected.includes(id)
                ? prevSelected.filter(productId => productId !== id)
                : [...prevSelected, id]
        );
    };

    const handleUpdateProduct = (id, updatedValues) => {
        setProducts((prevProducts) =>
            prevProducts.map(product =>
                product.id === id ? { ...product, ...updatedValues } : product
            )
        );

        // Also update in the accumulated products map
        const existing = allProductsRef.current.get(
            [...allProductsRef.current.entries()].find(([, p]) => p.id === id)?.[0]
        );
        if (existing) {
            allProductsRef.current.set(existing.name, { ...existing, ...updatedValues });
        }
    };

    const handlePayload = () => {
        // Use allProductsRef to include products from ALL pages, not just the current one
        const allProducts = Array.from(allProductsRef.current.values());
        const payload = allProducts
            .filter(product => selectedProductIds.includes(product.name))
            .map(product => {
                const payload = {
                    event_id: event?.id,
                    price: parseFloat(product.price),
                    name: product.name,
                    quantity: product.quantity,
                    description: product?.description,
                    type: 'product',
                    image_id: product.image_id,
                    sale_type: SaleTypes.BATCH
                }

                if (edit) {
                    const selected = event?.products?.find(el => el.name === product.name);

                    if (selected) {
                        payload.id = selected.id
                        payload.batches = [{
                            id: selected.batches[0].id,
                            is_active: selected.batches[0].is_active,
                            price: parseFloat(product.price),
                            name: product.name,
                            quantity: product.quantity,
                            description: product?.description,
                        }]
                    }
                }

                if (!payload?.image_id) {
                    delete payload.image_id;
                }

                return payload
            });

        if (!payload?.description) {
            delete payload.description;
        }

        if (!payload?.image_id) {
            delete payload.image_id;
        }

        const formatedPayload = payload.map(el => {
            let item = el;
            if (!item.description) {
                delete item.description
            }

            return item
        })

        return formatedPayload
    };

    async function updateProducts() {
        const payload = {
            type: 'products',
            event_id: event?.id,
            products: handlePayload()
        };

        setLoading(true);

        try {
            const { data } = await ticketsApi.updateBulk(payload);
            onSuccess({
                ...event,
                ...data,
            })
        } catch (error) {
            // console.log('error')
        } finally {
            setLoading(false);
        }
    }

    async function createProducts() {
        const payload = {
            products: handlePayload()
        };

        setLoading(true);

        if (!payload?.products?.length) {
            onSuccess(event);

            if (!isDraft) {
                removeDraft();
            }
            return
        }

        try {
            const { data } = await ticketsApi.createBulk(payload);
            onSuccess({
                ...event,
                ...data,
            })

            if (!isDraft) {
                removeDraft();
            }
        } catch (error) {
            // console.log('error')
            setLoading(false);
        }
    }

    async function removeDraft() {
        try {
            await ticketsApi.removeDraft(event?.slug);
        } catch (error) {
            // console.log('error')
        } finally {
            setLoading(false);
        }
    }

    function getSelectedProduct(name) {
        if (!selectedProductIds.includes(name)) {
            return null;
        }

        const product = event?.products?.find(p => p.name === name);

        if (!product) {
            return availableProducts.find(p => p.name === name);
        }

        return product?.batches?.[0];
    }



    return (
        <Container>
            <FullyCentered>
                <Text name='title' color={colors.occasionalPurple}>
                    Selecione os produtos do evento
                </Text>
            </FullyCentered>

            {
                loading ? (
                    <FullyCentered>
                        <Loader />
                    </FullyCentered>
                ) : (
                    <Column>
                        <ProductsList>
                            {
                                availableProducts?.map(el => (
                                    <ProductSelect
                                        data={el}
                                        key={el.id}
                                        edit={edit}
                                        selected={getSelectedProduct(el.name)}
                                        products={event?.products}
                                        onSelect={() => handleSelect(el.name)}
                                        onUpdate={handleUpdateProduct}
                                    />
                                ))
                            }
                        </ProductsList>
                        {productsPagination.lastPage > 1 && (
                            <Paginate
                                perPage={productsPagination.perPage}
                                totalItems={productsPagination.totalItems}
                                lastPage={productsPagination.lastPage}
                                firstPage={1}
                                onPaginate={onProductPaginate}
                                currentPage={productsPagination.currentPage}
                                marginTop="30px"
                            />
                        )}
                    </Column>
                )}

            <BottomBar>
                {
                    loading ? (
                        <LoaderWrapper>
                            <Loader />
                        </LoaderWrapper>
                    ) : (
                        <BottomBarContent>
                            <BackButton onClick={() => goBackFormStep(2)}>
                                <ArrowLeft size={16} />
                                Voltar
                            </BackButton>
                            <SubmitButton onClick={!edit ? createProducts : updateProducts}>
                                Avançar
                                <ArrowRight size={16} />
                            </SubmitButton>
                        </BottomBarContent>
                    )
                }
            </BottomBar>
        </Container >
    );
}

const Container = styled(Column)`
    overflow-x: auto;
    padding-bottom: 100px;
`

const ProductsList = styled(Column)`
    margin-top: 30px;
    
    @media screen and (min-width: ${breakpoints.md}){
        flex-direction: row;
        flex-wrap: wrap;

        & > div {
            width: 31.5%;
            margin-left: 1%;

            &:first-child {
                margin-left: 0;
            }
        }
       
    }
`;

const BottomBar = styled.div`
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    background: #fff;
    border-top: 1px solid ${colors.night}15;
    box-shadow: 0 -4px 20px ${colors.night}0A;
    z-index: 10;
    padding: 14px 24px;

    @media screen and (min-width: ${breakpoints.md}) {
        width: 75%;
    }
`;

const BottomBarContent = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
    max-width: 500px;
    margin: 0 auto;
`;

const LoaderWrapper = styled.div`
    display: flex;
    justify-content: center;
    padding: 8px 0;
`;

const BackButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    flex: 1;
    padding: 12px 20px;
    border: 1.5px solid ${colors.night}22;
    border-radius: 10px;
    background: transparent;
    color: ${colors.night};
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
        background: ${colors.night}08;
        border-color: ${colors.night}44;
    }
`;

const SubmitButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    flex: 1;
    padding: 12px 20px;
    border: none;
    border-radius: 10px;
    background: ${colors.occasionalPurple};
    color: #fff;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
        background: ${colors.secondaryPurple};
    }
`;